import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { clsx } from 'clsx';
import { 
  UsersIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';
import { campaignService } from '../../services/campaignService';
import { agentService, type AgentStatusInfo } from '../../services/agentService';
import { useAuth } from '../../contexts/AuthContext';
import { AdminUser as User, ParkSession, ConnectLatencyStats } from '../../types/admin';
import { AuditLog } from '../../types/audit';
import { PhoneNumber } from '../../types/campaign';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Table, Column, Pagination } from '../../components/UI/Table';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import Tabs from '../../components/UI/Tabs';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';

/** Seat ages are read at a glance — seconds while it matters, then minutes. */
function formatSeatAge(secs: number): string {
  if (secs < 90) return `${secs}s`;
  return `${Math.round(secs / 60)}m`;
}

function formatConnectMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

interface UserFilters {
  search: string;
  role?: 'admin' | 'user' | 'agent';
  verified?: boolean;
  page: number;
  limit: number;
}

interface AuditFilters {
  search: string;
  action?: string;
  entity?: string;
  page: number;
  limit: number;
}

const AdminPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'areaCodes'>('users');
  
  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userFilters, setUserFilters] = useState<UserFilters>({
    search: '',
    page: 1,
    limit: 50
  });
  const [usersTotal, setUsersTotal] = useState(0);
  const [showUserFilters, setShowUserFilters] = useState(false);

  // Create user modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    emailVerified: false,
    role: 'agent' as 'admin' | 'agent',
    phoneNumberId: '' as string,
  });
  const [creatingUser, setCreatingUser] = useState(false);

  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, AgentStatusInfo>>({});
  /** Live parked seats, by agent id. Absent means the agent has no leg up. */
  const [parkSessions, setParkSessions] = useState<Record<string, ParkSession>>({});
  const [connectLatency, setConnectLatency] = useState<ConnectLatencyStats | null>(null);
  const [humanCampaigns, setHumanCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [parkCampaignId, setParkCampaignId] = useState('');
  const [parkBulkSaving, setParkBulkSaving] = useState(false);
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState<AuditFilters>({
    search: '',
    page: 1,
    limit: 20
  });
  const [, setAuditTotal] = useState(0);
  const [showAuditFilters, setShowAuditFilters] = useState(false);
  const [areaCodeMappings, setAreaCodeMappings] = useState<Array<{
    area_code: string;
    timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific';
    source: string;
    updated_at: string;
  }>>([]);
  const [areaCodeLoading, setAreaCodeLoading] = useState(false);
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [areaCodeSearch, setAreaCodeSearch] = useState('');
  const [areaCodePage, setAreaCodePage] = useState(1);
  const [areaCodeLimit] = useState(50);
  const [areaCodeTotal, setAreaCodeTotal] = useState(0);
  const [areaCodeTotalPages, setAreaCodeTotalPages] = useState(1);
  const [areaCodeInput, setAreaCodeInput] = useState('');
  const [timezoneInput, setTimezoneInput] = useState<'Eastern' | 'Central' | 'Mountain' | 'Pacific'>('Eastern');
  const [bulkAreaCodeJson, setBulkAreaCodeJson] = useState('');
  
  // Modal state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editForm, setEditForm] = useState<{
    name: string;
    email: string;
    emailVerified: boolean;
    role: 'admin' | 'agent';
    phoneNumberId: string;
  }>({
    name: '',
    email: '',
    emailVerified: false,
    role: 'agent',
    phoneNumberId: '',
  });
  const [updatingUser, setUpdatingUser] = useState(false);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<AuditLog | null>(null);
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Removed restricted role actions and state

  useEffect(() => {
    if (location.pathname === '/admin/area-codes') {
      setActiveTab('areaCodes');
      return;
    }

    if (location.pathname === '/admin') {
      setActiveTab('users');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'audit') {
      fetchAuditLogs();
    } else {
      fetchAreaCodeMappings();
    }
  }, [activeTab, userFilters, auditFilters]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    let cancelled = false;
    campaignService
      .getCampaigns({ limit: 200 })
      .then((res) => {
        if (cancelled) return;
        const human = (res.campaigns || [])
          .filter((c) => c.campaignType === 'human')
          .map((c) => ({ id: c.id, name: c.name }));
        setHumanCampaigns(human);
      })
      .catch(() => {
        if (!cancelled) setHumanCampaigns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'users') return;

    const agentIds = users.filter((u) => u.role === 'agent').map((u) => u.id);
    if (agentIds.length === 0) {
      setAgentStatuses({});
      return;
    }

    const isPageVisible = () => (typeof document === 'undefined' ? true : document.visibilityState === 'visible');
    let pollTimer: number | undefined;
    let eventSource: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempts = 0;
    let closed = false;

    const pollStatuses = async () => {
      if (!isPageVisible()) return;
      try {
        const statuses = await agentService.getAgentStatuses(agentIds);
        setAgentStatuses(statuses);
      } catch (error) {
        // Ignore polling errors
      }
    };

    const startPolling = () => {
      if (pollTimer) return;
      pollStatuses();
      pollTimer = window.setInterval(() => {
        if (!isPageVisible()) return;
        pollStatuses();
      }, 8000);
    };

      const connectStream = async () => {
      if (typeof window === 'undefined' || !('EventSource' in window)) {
        startPolling();
        return;
      }

      if (!isPageVisible()) {
        startPolling();
        return;
      }

      const { getSseAccessToken } = await import('../../utils/sseAuthToken');
      const token = await getSseAccessToken();
      if (!token) {
        startPolling();
        return;
      }

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const fullUrl = baseUrl.startsWith('http') ? `${baseUrl}/agents/status/stream` : `${window.location.origin}${baseUrl}/agents/status/stream`;
      const url = new URL(fullUrl);
      url.searchParams.set('agentIds', agentIds.join(','));
      url.searchParams.set('token', token);

      eventSource = new EventSource(url.toString());

      const handleStatus = (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          if (payload?.statuses) {
            setAgentStatuses(payload.statuses);
          }
        } catch (error) {
          // Ignore parse errors
        }
      };

      eventSource.addEventListener('status', handleStatus);
      eventSource.addEventListener('message', handleStatus);
      eventSource.addEventListener('error', () => {
        if (closed) return;
        eventSource?.close();
        eventSource = null;
        scheduleReconnect();
      });
    };

    const scheduleReconnect = () => {
      if (closed || reconnectTimer) return;
      if (!isPageVisible()) return;
      reconnectAttempts += 1;
      const delay = Math.min(30000, 1000 * 2 ** (reconnectAttempts - 1));
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        void connectStream();
      }, delay);

      if (!pollTimer) {
        startPolling();
      }
    };

    const handleVisibilityChange = () => {
      if (closed) return;
      if (isPageVisible()) {
        if (!eventSource) {
          void connectStream();
        }
        if (!pollTimer) {
          startPolling();
        }
      } else if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };

    void connectStream();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      closed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [activeTab, users]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await adminService.getUsers({
        search: userFilters.search,
        role: userFilters.role,
        page: userFilters.page,
        limit: userFilters.limit,
      });

      const list = response?.data?.users ?? [];
      const total = response?.data?.pagination?.total ?? list.length;

      setUsers(list);
      setUsersTotal(total);

      const agentIds = list.filter((u) => u.role === 'agent').map((u) => u.id);
      if (agentIds.length > 0) {
        const statuses = await agentService.getAgentStatuses(agentIds);
        setAgentStatuses(statuses);
      } else {
        setAgentStatuses({});
      }

      // Whether an agent parks is a setting; whether they currently hold a leg
      // is not. Both belong in the same column — the setting is what you change,
      // the seat is what tells you whether it took.
      try {
        const sessions = await adminService.getParkSessions();
        setParkSessions(Object.fromEntries(sessions.map((s) => [s.agentId, s])));
      } catch {
        setParkSessions({});
      }

      try {
        setConnectLatency(await adminService.getConnectLatency());
      } catch {
        setConnectLatency(null);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchAreaCodeMappings = async (options?: { page?: number; search?: string }) => {
    const requestedPage = options?.page ?? areaCodePage;
    const requestedSearch = options?.search ?? areaCodeSearch;
    try {
      setAreaCodeLoading(true);
      const response = await adminService.getAreaCodeTimezones({
        search: requestedSearch || undefined,
        page: requestedPage,
        limit: areaCodeLimit,
      });
      setAreaCodeMappings(response.mappings || []);
      setAreaCodePage(response.pagination?.page || requestedPage);
      setAreaCodeTotal(response.pagination?.total || 0);
      setAreaCodeTotalPages(response.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch area code mappings:', error);
      toast.error('Failed to load area code mappings');
    } finally {
      setAreaCodeLoading(false);
    }
  };

  const handleAreaCodePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > areaCodeTotalPages || areaCodeLoading) return;
    fetchAreaCodeMappings({ page: nextPage });
  };

  /** Instant-connect parked leg, per agent. Optimistic so the toggle feels immediate. */
  const [parkSaving, setParkSaving] = useState<string | null>(null);
  const toggleAgentPark = useCallback(async (agentId: string, next: boolean) => {
    setParkSaving(agentId);
    setUsers((prev) => prev.map((u) => (u.id === agentId ? { ...u, park_enabled: next } : u)));
    try {
      await adminService.setAgentParkEnabled(agentId, next);
      toast.success(next ? 'Instant connect enabled' : 'Instant connect disabled');
    } catch (err: any) {
      setUsers((prev) => prev.map((u) => (u.id === agentId ? { ...u, park_enabled: !next } : u)));
      toast.error(err?.message || 'Could not change instant connect');
    } finally {
      setParkSaving(null);
    }
  }, []);

  /**
   * Drop a seat by hand.
   *
   * Only worth reaching for when a seat is stuck in a state its age says it
   * should have left — the reconciler heals most of those within a minute.
   */
  const [unparking, setUnparking] = useState<string | null>(null);
  const forceUnpark = useCallback(async (agentId: string) => {
    setUnparking(agentId);
    try {
      await adminService.unparkAgent(agentId);
      setParkSessions((prev) => {
        const next = { ...prev };
        delete next[agentId];
        return next;
      });
      toast.success('Parked leg dropped — the agent re-parks on their next heartbeat');
    } catch (err: any) {
      toast.error(err?.message || 'Could not drop the parked leg');
    } finally {
      setUnparking(null);
    }
  }, []);

  const toggleCampaignPark = async (enabled: boolean) => {
    if (!parkCampaignId) {
      toast.error('Pick a campaign first');
      return;
    }
    const label = humanCampaigns.find((c) => c.id === parkCampaignId)?.name || 'this campaign';
    const ok = window.confirm(
      enabled
        ? `Turn instant connect on for every agent on “${label}”? They park a leg the next time they go Available.`
        : `Turn instant connect off for every agent on “${label}”? Open parked legs hang up immediately.`
    );
    if (!ok) return;
    setParkBulkSaving(true);
    try {
      const updated = await adminService.setCampaignParkEnabled(parkCampaignId, enabled);
      toast.success(
        enabled
          ? `Instant connect enabled for ${updated} agent${updated === 1 ? '' : 's'}`
          : `Instant connect disabled for ${updated} agent${updated === 1 ? '' : 's'}`
      );
      await fetchUsers();
    } catch (err: any) {
      toast.error(err?.message || 'Could not change instant connect for the campaign');
    } finally {
      setParkBulkSaving(false);
    }
  };

  const PARK_STATE_STYLE: Record<ParkSession['state'], string> = {
    idle: 'bg-success/10 text-success',
    reserved: 'bg-warning/10 text-warning',
    bridged: 'bg-primary/10 text-primary',
    wrapup: 'bg-muted text-muted-foreground',
  };

  const getAgentStatusBadge = (info?: AgentStatusInfo) => {
    const config: Record<string, { label: string; className: string }> = {
      available: { label: 'Available', className: 'bg-success/10 text-success' },
      ringing: { label: 'Ringing', className: 'bg-warning/10 text-warning' },
      'in-call': { label: 'In Call', className: 'bg-secondary/20 text-secondary-foreground' },
      // The API returns this after a call ends and before the agent has
      // dispositioned. Without an entry it fell through to Offline, which reads
      // as "this agent is gone" when they are in fact mid-wrap-up.
      'wrap-up': { label: 'Wrap-up', className: 'bg-muted text-muted-foreground' },
      offline: { label: 'Offline', className: 'bg-muted text-muted-foreground' },
    };

    // Falls back to offline only when the agent is genuinely unknown — an
    // unrecognised status is shown as-is rather than silently reported as
    // Offline, so a new state added on the server cannot hide behind this.
    const resolved = info?.status
      ? config[info.status] || { label: info.status, className: 'bg-muted text-muted-foreground' }
      : config.offline!;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${resolved.className}`}>
        {resolved.label}
      </span>
    );
  };

  const loadPhoneNumbers = async () => {
    if (phoneNumbersLoading) return;
    setPhoneNumbersLoading(true);
    try {
      const numbers = await campaignService.getPhoneNumbers();
      setPhoneNumbers(numbers || []);
    } catch (error) {
      console.error('Failed to load phone numbers:', error);
      toast.error('Failed to load phone numbers');
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  const getAvailableNumbers = (currentId?: string) => {
    const assignedIds = new Set(
      users.map((u) => u.phone_number_id).filter((id): id is string => !!id)
    );

    return phoneNumbers
      .filter((n) => !assignedIds.has(n.id) || n.id === currentId);
  };

  useEffect(() => {
    const needsPhoneNumbers =
      (showCreateUserModal && createForm.role === 'agent') ||
      (showEditUserModal && editForm.role === 'agent');

    if (needsPhoneNumbers && phoneNumbers.length === 0) {
      loadPhoneNumbers();
    }
  }, [showCreateUserModal, showEditUserModal, createForm.role, editForm.role, phoneNumbers.length]);

  useEffect(() => {
    if (showCreateUserModal) {
      if (createForm.role === 'agent') {
        const available = getAvailableNumbers(createForm.phoneNumberId);
        if (!createForm.phoneNumberId && available.length > 0) {
          setCreateForm((prev) => ({ ...prev, phoneNumberId: available[0]!.id }));
        }
      } else if (createForm.phoneNumberId) {
        setCreateForm((prev) => ({ ...prev, phoneNumberId: '' }));
      }
    }
  }, [showCreateUserModal, createForm.role, phoneNumbers, users]);


  useEffect(() => {
    if (showEditUserModal && selectedUser) {
      if (editForm.role === 'agent') {
        const available = getAvailableNumbers(editForm.phoneNumberId || selectedUser.phone_number_id || undefined);
        if (!editForm.phoneNumberId && available.length > 0) {
          setEditForm((prev) => ({ ...prev, phoneNumberId: available[0]!.id }));
        }
      } else if (editForm.phoneNumberId) {
        setEditForm((prev) => ({ ...prev, phoneNumberId: '' }));
      }
    }
  }, [showEditUserModal, editForm.role, phoneNumbers, users, selectedUser]);

  const formatPhoneOption = (num: PhoneNumber) => ({
    value: num.id,
    label: (num.name ? `${num.name} — ` : '') + num.number,
  });

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '40px',
      borderColor: 'hsl(var(--input))',
      backgroundColor: 'hsl(var(--background))',
      boxShadow: 'none',
      ':hover': { borderColor: 'hsl(var(--input))' },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'hsl(var(--background))',
      zIndex: 50,
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
    menuList: (base: any) => ({
      ...base,
      backgroundColor: 'hsl(var(--background))',
      color: 'hsl(var(--foreground))',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'transparent',
      color: 'hsl(var(--foreground))',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'hsl(var(--foreground))',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: 'hsl(var(--muted-foreground))',
    }),
    input: (base: any) => ({
      ...base,
      color: 'hsl(var(--foreground))',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: 'hsl(var(--muted-foreground))',
      ':hover': { color: 'hsl(var(--foreground))' },
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      backgroundColor: 'hsl(var(--border))',
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: '2px 10px',
    }),
  };

  const fetchAuditLogs = async () => {
    try {
      setAuditLoading(true);
      // Mock data for now
      const mockAuditLogs: AuditLog[] = [
        {
          id: '1',
          actor_id: '1',
          owner_type: 'user',
          owner_id: '1',
          action: 'campaign.created',
          entity: 'campaign',
          entity_id: 'camp_123',
          metadata: {
            campaign_name: 'Q4 Sales Campaign',
            leads_count: 150
          },
          created_at: new Date().toISOString(),
          actor: {
            id: '1',
            name: 'Admin User',
            email: 'admin@example.com'
          }
        },
        {
          id: '2',
          actor_id: '2',
          owner_type: 'user',
          owner_id: '2',
          action: 'lead.imported',
          entity: 'lead',
          entity_id: 'lead_456',
          metadata: {
            import_count: 50,
            file_name: 'leads.csv'
          },
          created_at: new Date(Date.now() - 3600000).toISOString(),
          actor: {
            id: '2',
            name: 'John Doe',
            email: 'john@example.com'
          }
        },
        {
          id: '3',
          actor_id: '1',
          owner_type: 'user',
          owner_id: '3',
          action: 'user.role_changed',
          entity: 'user',
          entity_id: '3',
          metadata: {
            old_role: 'user',
            new_role: 'admin'
          },
          created_at: new Date(Date.now() - 7200000).toISOString(),
          actor: {
            id: '1',
            name: 'Admin User',
            email: 'admin@example.com'
          }
        }
      ];
      
      setAuditLogs(mockAuditLogs);
      setAuditTotal(mockAuditLogs.length);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleUserSearch = (search: string) => {
    setUserFilters(prev => ({ ...prev, search, page: 1 }));
  };

  /**
   * The users table asked the server for one page and rendered it as if it were
   * everything — no control moved `page` off 1, and the `total` the server sent
   * back was read into a state setter nobody read. An admin with more than one
   * page of users saw the newest `limit` of them and no sign the rest existed.
   */
  const usersTotalPages = Math.max(1, Math.ceil(usersTotal / userFilters.limit));
  const handleUserPageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > usersTotalPages || usersLoading) return;
    setUserFilters(prev => ({ ...prev, page: nextPage }));
  };

  const handleAuditSearch = (search: string) => {
    setAuditFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      admin: { color: 'bg-secondary/20 text-secondary-foreground', icon: ShieldCheckIcon },
      agent: { color: 'bg-indigo-500/10 text-indigo-600', icon: UsersIcon },
      user: { color: 'bg-primary/10 text-primary', icon: UsersIcon },
    } as const;

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.user;
    const Icon = config.icon as any;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="-ml-0.5 mr-1.5 h-4 w-4" />
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const getActionBadge = (action: string) => {
    const actionConfig = {
      created: { color: 'bg-success/10 text-success' },
      updated: { color: 'bg-primary/10 text-primary' },
      deleted: { color: 'bg-destructive/10 text-destructive' },
      imported: { color: 'bg-secondary/20 text-secondary-foreground' },
      exported: { color: 'bg-warning/10 text-warning' },
    };

    const actionType = action.split('.')[1] || action;
    const config = actionConfig[actionType as keyof typeof actionConfig] || { color: 'bg-muted text-muted-foreground' };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {action}
      </span>
    );
  };

  const openUserModal = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const openAuditModal = (auditLog: AuditLog) => {
    setSelectedAuditLog(auditLog);
    setShowAuditModal(true);
  };

  const handleSaveAreaCode = async () => {
    const code = areaCodeInput.trim();
    if (!/^\d{3}$/.test(code)) {
      toast.error('Area code must be 3 digits');
      return;
    }
    try {
      await adminService.upsertAreaCodeTimezone(code, timezoneInput);
      toast.success('Mapping saved');
      setAreaCodeInput('');
      fetchAreaCodeMappings({ page: areaCodePage });
    } catch (error) {
      toast.error('Failed to save mapping');
    }
  };

  const handleDeleteAreaCode = async (areaCode: string) => {
    try {
      await adminService.deleteAreaCodeTimezone(areaCode);
      toast.success('Mapping deleted');
      fetchAreaCodeMappings({ page: areaCodePage });
    } catch {
      toast.error('Failed to delete mapping');
    }
  };

  const handleBulkImportAreaCodes = async () => {
    try {
      const parsed = JSON.parse(bulkAreaCodeJson || '[]');
      if (!Array.isArray(parsed) || parsed.length === 0) {
        toast.error('Provide a JSON array with mappings');
        return;
      }
      const mappings = parsed.map((row: any) => ({
        areaCode: String(row.areaCode ?? row.area_code ?? '').trim(),
        timezone: String(row.timezone) as 'Eastern' | 'Central' | 'Mountain' | 'Pacific',
      }));
      const result = await adminService.bulkUpsertAreaCodeTimezones(mappings);
      toast.success(`Imported ${result.imported} mappings`);
      setBulkAreaCodeJson('');
      fetchAreaCodeMappings({ page: 1 });
    } catch (error) {
      toast.error('Invalid JSON or import failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Administration</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage users and view system audit logs.
        </p>
      </div>

      {/* Tabs */}
      <Tabs
        variant="segmented"
        tabs={[
          {
            id: 'users',
            label: 'Users',
            icon: UsersIcon,
            count: users.length
          },
          {
            id: 'audit',
            label: 'Audit Logs',
            icon: ClipboardDocumentListIcon,
            count: auditLogs.length
          },
          {
            id: 'areaCodes',
            label: 'Area Codes',
            icon: ShieldCheckIcon,
            count: areaCodeMappings.length
          }
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as 'users' | 'audit' | 'areaCodes')}
      />

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Instant connect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-foreground">Answer → audio</p>
                <p className="text-xs text-muted-foreground mb-2">
                  How long the customer waits after picking up. Park target is under 500 ms.
                </p>
                {connectLatency && connectLatency.count > 0 ? (
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span><span className="text-muted-foreground">p50</span> {formatConnectMs(connectLatency.p50)}</span>
                    <span><span className="text-muted-foreground">p95</span> {formatConnectMs(connectLatency.p95)}</span>
                    <span><span className="text-muted-foreground">n</span> {connectLatency.count}</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No samples yet — appears after human auto-dial connects.</p>
                )}
              </div>
              {currentUser?.role === 'admin' && (
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-medium text-foreground mb-2">Roll out by campaign</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label htmlFor="park-campaign" className="sr-only">Campaign</label>
                    <select
                      id="park-campaign"
                      value={parkCampaignId}
                      onChange={(e) => setParkCampaignId(e.target.value)}
                      className="select sm:max-w-xs"
                    >
                      <option value="">Select a human campaign</option>
                      {humanCampaigns.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        disabled={!parkCampaignId || parkBulkSaving}
                        onClick={() => void toggleCampaignPark(true)}
                      >
                        Enable for all agents
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        size="sm"
                        disabled={!parkCampaignId || parkBulkSaving}
                        onClick={() => void toggleCampaignPark(false)}
                      >
                        Disable for all agents
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users Filters */}
          <Card>
            <CardContent className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <input
                      type="text"
                      value={userFilters.search}
                      onChange={(e) => handleUserSearch(e.target.value)}
                      className="input pl-10"
                      placeholder="Search users by name or email..."
                    />
                  </div>
                </div>
                {currentUser?.role === 'admin' && (
                  <Button
                    onClick={() => setShowCreateUserModal(true)}
                    variant="primary"
                    size="sm"
                    className="ml-4"
                  >
                    Create User
                  </Button>
                )}
                <Button
                  onClick={() => setShowUserFilters(!showUserFilters)}
                  variant="outlined"
                  size="sm"
                  className="ml-4"
                >
                  <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Filters
                </Button>
              </div>

              {showUserFilters && (
                <div className="border-t border-border pt-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Role
                      </label>
                      <select
                        value={userFilters.role || ''}
                        onChange={(e) =>
                          setUserFilters(prev => ({
                            ...prev,
                            role: e.target.value === '' ? undefined : (e.target.value as 'admin' | 'agent'),
                            page: 1,
                          }))
                        }
                        className="select"
                      >
                        <option value="">All Roles</option>
                        <option value="admin">Admin</option>
                        <option value="agent">Agent</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Email Verification
                      </label>
                      <select
                        value={userFilters.verified === undefined ? '' : userFilters.verified.toString()}
                        onChange={(e) => setUserFilters(prev => ({ 
                          ...prev, 
                          verified: e.target.value === '' ? undefined : e.target.value === 'true',
                          page: 1 
                        }))}
                        className="select"
                      >
                        <option value="">All Users</option>
                        <option value="true">Verified</option>
                        <option value="false">Unverified</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card className="overflow-hidden">
            <Table
              data={users}
              columns={[
                {
                  key: 'user',
                  header: 'User',
                  render: (user) => (
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {user.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {user.email}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'role',
                  header: 'Role',
                  render: (user) => getRoleBadge(user.role)
                },
                {
                  key: 'phoneNumber',
                  header: 'WebRTC',
                  render: (user) => (
                    <span className="text-sm text-muted-foreground">
                      {user.role === 'agent'
                        ? (user.phone_number_provider === 'telnyx_webrtc'
                          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Ready</span>
                          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pending</span>)
                        : '—'}
                    </span>
                  )
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (user) => (
                    <div className="flex flex-col gap-1">
                      {user.email_verified_at ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                          <ExclamationTriangleIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                          Unverified
                        </span>
                      )}
                      {user.role === 'agent' && getAgentStatusBadge(agentStatuses[user.id])}
                    </div>
                  )
                },
                {
                  key: 'park',
                  header: 'Instant connect',
                  render: (user) => (
                    user.role !== 'agent' ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <button
                        type="button"
                        disabled={parkSaving === user.id}
                        onClick={() => void toggleAgentPark(user.id, !user.park_enabled)}
                        title={user.park_enabled
                          ? 'Agent holds an open line; calls connect instantly'
                          : 'Agent is rung for each call (slower connect)'}
                        className={clsx(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50',
                          user.park_enabled
                            ? 'bg-success/10 text-success hover:bg-success/20'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        )}
                      >
                        {user.park_enabled ? 'On' : 'Off'}
                      </button>
                    )
                  )
                },
                {
                  key: 'park_seat',
                  header: 'Seat',
                  render: (user) => {
                    const seat = parkSessions[user.id];
                    if (user.role !== 'agent' || !user.park_enabled) {
                      return <span className="text-xs text-muted-foreground">—</span>;
                    }
                    if (!seat) {
                      return (
                        <span
                          className="text-xs text-muted-foreground"
                          title="No parked leg. The agent is not dialable until their browser parks one."
                        >
                          none
                        </span>
                      );
                    }
                    return (
                      <div className="flex items-center gap-2">
                        <span
                          className={clsx(
                            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                            PARK_STATE_STYLE[seat.state]
                          )}
                          title={seat.conferenceName || undefined}
                        >
                          {seat.state}
                          {seat.ageSecs !== null && ` · ${formatSeatAge(seat.ageSecs)}`}
                        </span>
                        <button
                          type="button"
                          disabled={unparking === user.id}
                          onClick={() => void forceUnpark(user.id)}
                          title="Drop this leg. The agent re-parks on their next heartbeat."
                          className="text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
                        >
                          Drop
                        </button>
                      </div>
                    );
                  }
                },
                {
                  key: 'created',
                  header: 'Created',
                  render: (user) => (
                    <span className="text-sm text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString()}
                    </span>
                  )
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right' as const,
                  render: (user) => (
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        onClick={() => openUserModal(user)}
                        variant="outlined"
                        size="sm"
                        title="View details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                      {currentUser?.role === 'admin' && (
                      <Button
                        variant="outlined"
                        size="sm"
                        title="Edit user"
                        onClick={() => {
                          setSelectedUser(user);
                          setEditForm({
                            name: user.name,
                            email: user.email,
                            emailVerified: !!user.email_verified_at,
                            role: (user.role === 'admin' ? 'admin' : 'agent') as 'admin' | 'agent',
                            phoneNumberId: user.phone_number_id || '',
                          });
                          setResetPasswordValue('');
                          setShowEditUserModal(true);
                        }}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Button>
                      )}
                      {/* Restrict/Unrestrict actions removed */}
                      {currentUser?.role === 'admin' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          title="Delete user"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteUserModal(true);
                          }}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )
                }
              ] as Column<User>[]}
              loading={usersLoading}
              emptyState={
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                    <UsersIcon className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground">No users found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No users match your current filters.
                  </p>
                </div>
              }
            />
            <Pagination
              currentPage={userFilters.page}
              totalPages={usersTotalPages}
              totalItems={usersTotal}
              itemsPerPage={userFilters.limit}
              onPageChange={handleUserPageChange}
            />
          </Card>
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Audit Filters */}
          <Card>
            <CardContent className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <input
                      type="text"
                      value={auditFilters.search}
                      onChange={(e) => handleAuditSearch(e.target.value)}
                      className="input pl-10"
                      placeholder="Search audit logs..."
                    />
                  </div>
                </div>
                <Button
                  onClick={() => setShowAuditFilters(!showAuditFilters)}
                  variant="outlined"
                  size="sm"
                  className="ml-4"
                >
                  <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
                  Filters
                </Button>
              </div>

              {showAuditFilters && (
                <div className="border-t border-border pt-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Action
                      </label>
                      <select
                        value={auditFilters.action || ''}
                        onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value || undefined, page: 1 }))}
                        className="select"
                      >
                        <option value="">All Actions</option>
                        <option value="created">Created</option>
                        <option value="updated">Updated</option>
                        <option value="deleted">Deleted</option>
                        <option value="imported">Imported</option>
                        <option value="exported">Exported</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Entity
                      </label>
                      <select
                        value={auditFilters.entity || ''}
                        onChange={(e) => setAuditFilters(prev => ({ ...prev, entity: e.target.value || undefined, page: 1 }))}
                        className="select"
                      >
                        <option value="">All Entities</option>
                        <option value="user">User</option>
                        <option value="campaign">Campaign</option>
                        <option value="lead">Lead</option>
                        <option value="call">Call</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Audit Logs Table */}
          <Card className="overflow-hidden">
            <Table
              data={auditLogs}
              columns={[
                {
                  key: 'actor',
                  header: 'Actor',
                  render: (log) => (
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {log.actor?.name || 'Unknown'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {log.actor?.email || 'No email'}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'action',
                  header: 'Action',
                  render: (log) => getActionBadge(log.action)
                },
                {
                  key: 'entity',
                  header: 'Entity',
                  render: (log) => (
                    <div>
                      <div className="text-sm font-medium text-foreground capitalize">
                        {log.entity}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {log.entity_id}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'timestamp',
                  header: 'Timestamp',
                  render: (log) => (
                    <span className="text-sm text-muted-foreground">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  )
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right' as const,
                  render: (log) => (
                    <Button
                      onClick={() => openAuditModal(log)}
                      variant="outlined"
                      size="sm"
                      title="View details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </Button>
                  )
                }
              ] as Column<AuditLog>[]}
              loading={auditLoading}
              emptyState={
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <ClipboardDocumentListIcon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-foreground">No audit logs found</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No audit logs match your current filters.
                  </p>
                </div>
              }
            />
          </Card>
        </div>
      )}

      {activeTab === 'areaCodes' && (
        <div className="space-y-6">
          <Card>
            <CardContent className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-base font-semibold text-foreground">Area Code Mapping Management</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage area code → timezone mappings. Use "Backfill Leads" to enrich existing leads that are missing area codes.
                  </p>
                </div>
                <Button
                  variant="primary"
                  disabled={syncAllLoading}
                  onClick={async () => {
                    if (!confirm('This will sync area codes for ALL leads missing them across all campaigns. Continue?')) return;
                    setSyncAllLoading(true);
                    try {
                      const result = await adminService.backfillLeadsAreaCode();
                      toast.success(`Synced area codes for ${result.areaCodeFilled} leads.`);
                    } catch (err: any) {
                      toast.error(err?.response?.data?.message || 'Sync failed');
                    } finally {
                      setSyncAllLoading(false);
                    }
                  }}
                >
                  {syncAllLoading ? 'Syncing...' : 'Sync All Leads Area Codes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Search & Filter</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <input
                  value={areaCodeSearch}
                  onChange={(e) => setAreaCodeSearch(e.target.value)}
                  className="input"
                  placeholder="Search by area code or timezone"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAreaCodePage(1);
                      fetchAreaCodeMappings({ page: 1 });
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setAreaCodeSearch('');
                      setAreaCodePage(1);
                      fetchAreaCodeMappings({ page: 1, search: '' });
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Add / Update Single Mapping</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <input
                  value={areaCodeInput}
                  onChange={(e) => setAreaCodeInput(e.target.value.replace(/\D/g, ''))}
                  className="input"
                  maxLength={3}
                  placeholder="Area code (e.g. 305)"
                />
                <select
                  value={timezoneInput}
                  onChange={(e) => setTimezoneInput(e.target.value as 'Eastern' | 'Central' | 'Mountain' | 'Pacific')}
                  className="select"
                >
                  <option value="Eastern">Eastern</option>
                  <option value="Central">Central</option>
                  <option value="Mountain">Mountain</option>
                  <option value="Pacific">Pacific</option>
                </select>
                <div className="flex justify-end">
                  <Button variant="primary" onClick={handleSaveAreaCode}>Save Mapping</Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Bulk JSON Import</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Expected format: <code>[{"{"}"areaCode":"305","timezone":"Eastern"{"}"}, {"{"}"areaCode":"214","timezone":"Central"{"}"}]</code>
              </p>
              <textarea
                value={bulkAreaCodeJson}
                onChange={(e) => setBulkAreaCodeJson(e.target.value)}
                className="textarea w-full"
                rows={5}
                placeholder='[{"areaCode":"305","timezone":"Eastern"},{"areaCode":"214","timezone":"Central"}]'
              />
              <div className="flex justify-end">
                <Button variant="primary" onClick={handleBulkImportAreaCodes}>
                  Import JSON
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Current Mappings ({areaCodeTotal.toLocaleString()})</CardTitle>
            </CardHeader>
            <Table
              data={areaCodeMappings}
              loading={areaCodeLoading}
              columns={[
                { key: 'area_code', header: 'Area Code', render: (row) => <span className="font-medium">{row.area_code}</span> },
                { key: 'timezone', header: 'Timezone', render: (row) => row.timezone },
                { key: 'source', header: 'Source', render: (row) => row.source || '—' },
                { key: 'updated_at', header: 'Updated', render: (row) => new Date(row.updated_at).toLocaleString() },
                {
                  key: 'actions',
                  header: '',
                  align: 'right' as const,
                  render: (row) => (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteAreaCode(row.area_code)}
                    >
                      Delete
                    </Button>
                  ),
                },
              ]}
            />
            <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">
                Page {areaCodePage} of {areaCodeTotalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => handleAreaCodePageChange(areaCodePage - 1)}
                  disabled={areaCodePage <= 1 || areaCodeLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => handleAreaCodePageChange(areaCodePage + 1)}
                  disabled={areaCodePage >= areaCodeTotalPages || areaCodeLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* User Details Modal */}
      <Modal
        isOpen={showUserModal && !!selectedUser}
        onClose={() => setShowUserModal(false)}
        title="User Details"
        size="lg"
      >
        {selectedUser && (
          <div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Name</label>
                    <p className="mt-1 text-sm text-foreground">{selectedUser.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Email</label>
                    <p className="mt-1 text-sm text-foreground">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Role</label>
                    <div className="mt-1">{getRoleBadge(selectedUser.role)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">WebRTC Status</label>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedUser.role === 'agent'
                        ? (selectedUser.phone_number_provider === 'telnyx_webrtc' ? 'Ready (auto-provisioned)' : 'Pending — will provision on first login')
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Email Verified</label>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedUser.email_verified_at ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Created</label>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(selectedUser.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(selectedUser.updated_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
              
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setShowUserModal(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        title="Create New User"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Name</label>
              <input
                type="text"
                className="input"
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email</label>
              <input
                type="email"
                className="input"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Password</label>
              <input
                type="password"
                className="input"
                value={createForm.password}
                onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 8 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Role</label>
              <select
                className="select"
                value={createForm.role}
                onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value as 'admin' | 'agent' }))}
              >
                <option value="agent">Agent</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                id="create-user-verified"
                type="checkbox"
                className="checkbox"
                checked={createForm.emailVerified}
                onChange={(e) => setCreateForm(prev => ({ ...prev, emailVerified: e.target.checked }))}
              />
              <label htmlFor="create-user-verified" className="text-sm font-medium text-foreground">
                Verified
              </label>
            </div>
            {createForm.role === 'agent' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Phone Number <span className="text-muted-foreground text-xs">(Optional — WebRTC auto-provisioned)</span></label>
                {(() => {
                  const available = getAvailableNumbers(createForm.phoneNumberId);
                  const options = available.map(formatPhoneOption);
                  const value = options.find((opt) => opt.value === createForm.phoneNumberId) || null;

                  return (
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={selectStyles}
                      isSearchable
                      isLoading={phoneNumbersLoading}
                      options={options}
                      value={value}
                      placeholder={phoneNumbersLoading ? 'Loading numbers...' : 'Select a number...'}
                      noOptionsMessage={() => 'No available phone numbers'}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                      onChange={(option) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          phoneNumberId: option ? (option as any).value : '',
                        }))
                      }
                    />
                  );
                })()}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              onClick={() => setShowCreateUserModal(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (creatingUser) return;
                setCreatingUser(true);
                try {
                  const selectedPhone = phoneNumbers.find((num) => num.id === createForm.phoneNumberId);
                  await adminService.createUser({
                    name: createForm.name.trim(),
                    email: createForm.email.trim(),
                    password: createForm.password,
                    emailVerified: createForm.emailVerified,
                    role: createForm.role,
                    phoneNumberId: createForm.role === 'agent' ? selectedPhone?.id : undefined,
                    phoneNumber: createForm.role === 'agent' ? selectedPhone?.number : undefined,
                    phoneNumberProvider: createForm.role === 'agent' ? selectedPhone?.provider : undefined,
                  });
                  setShowCreateUserModal(false);
                  setCreateForm({
                    name: '',
                    email: '',
                    password: '',
                    emailVerified: false,
                    role: 'agent',
                    phoneNumberId: '',
                  });
                  fetchUsers();
                } catch (error) {
                  console.error('Failed to create user:', error);
                  let errorMessage = 'Failed to create user';
                  const anyErr: any = error;
                  if (anyErr?.response?.data?.error?.message) {
                    errorMessage = anyErr.response.data.error.message;
                  } else if (anyErr?.response?.data?.message) {
                    errorMessage = anyErr.response.data.message;
                  } else if (anyErr?.message) {
                    errorMessage = anyErr.message;
                  }
                  toast.error(errorMessage);
                } finally {
                  setCreatingUser(false);
                }
              }}
              disabled={
                creatingUser ||
                !createForm.name ||
                !createForm.email ||
                !createForm.password
              }
            >
              {creatingUser ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal (Admin-only) */}
      <Modal
        isOpen={showEditUserModal && !!selectedUser}
        onClose={() => setShowEditUserModal(false)}
        title="Edit user"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                <input
                  type="text"
                  className="input"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                <input
                  type="email"
                  className="input"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Role</label>
                <select
                  className="select"
                  value={editForm.role}
                  onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value as 'admin' | 'agent' }))}
                >
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  id="emailVerified"
                  type="checkbox"
                  className="checkbox"
                  checked={!!editForm.emailVerified}
                  onChange={(e) => setEditForm(prev => ({ ...prev, emailVerified: e.target.checked }))}
                />
                <label htmlFor="emailVerified" className="text-sm font-medium text-foreground">Verified</label>
              </div>
              {editForm.role === 'agent' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                  {(() => {
                    const available = getAvailableNumbers(
                      editForm.phoneNumberId || selectedUser.phone_number_id || undefined
                    );
                    const hasCurrent =
                      !!selectedUser.phone_number_id &&
                      !available.some((num) => num.id === selectedUser.phone_number_id);
                    const options = [
                      ...(hasCurrent
                        ? [
                            {
                              value: selectedUser.phone_number_id!,
                              label: `${selectedUser.phone_number || 'Current number'} (current)`,
                            },
                          ]
                        : []),
                      ...available.map(formatPhoneOption),
                    ];
                    const value =
                      options.find((opt) => opt.value === editForm.phoneNumberId) ||
                      (selectedUser.phone_number_id
                        ? options.find((opt) => opt.value === selectedUser.phone_number_id)
                        : null) ||
                      null;

                    return (
                      <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        isSearchable
                        isLoading={phoneNumbersLoading}
                        options={options}
                        value={value}
                        placeholder={phoneNumbersLoading ? 'Loading numbers...' : 'Select a number...'}
                        noOptionsMessage={() => 'No available phone numbers'}
                        menuPosition="fixed"
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                        onChange={(option) =>
                          setEditForm((prev) => ({
                            ...prev,
                            phoneNumberId: option ? (option as any).value : '',
                          }))
                        }
                      />
                    );
                  })()}
                </div>
              )}
            </div>
            {/* Reset Password Section */}
            <div className="border-t border-border pt-4 mt-4">
              <label className="block text-sm font-medium text-foreground mb-1">Reset Password</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  className="input flex-1"
                  placeholder="New password (min 8 characters)"
                  value={resetPasswordValue}
                  onChange={(e) => setResetPasswordValue(e.target.value)}
                />
                <Button
                  variant="outlined"
                  size="sm"
                  disabled={resettingPassword || resetPasswordValue.length < 8}
                  onClick={async () => {
                    if (resettingPassword || !selectedUser) return;
                    setResettingPassword(true);
                    try {
                      await adminService.resetUserPassword(selectedUser.id, resetPasswordValue);
                      toast.success(`Password reset for ${selectedUser.email}`);
                      setResetPasswordValue('');
                    } catch (error: any) {
                      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Failed to reset password';
                      toast.error(msg);
                    } finally {
                      setResettingPassword(false);
                    }
                  }}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset'}
                </Button>
              </div>
              {resetPasswordValue.length > 0 && resetPasswordValue.length < 8 && (
                <p className="text-xs text-destructive mt-1">Password must be at least 8 characters</p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outlined" onClick={() => { setShowEditUserModal(false); setResetPasswordValue(''); }}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (updatingUser) return;
                  setUpdatingUser(true);
                  try {
                    const payload: Partial<User> & { emailVerified?: boolean } = {};
                    if (editForm.name.trim() !== selectedUser.name) payload.name = editForm.name.trim();
                    if (editForm.email.trim() !== selectedUser.email) payload.email = editForm.email.trim();
                    if (editForm.role !== selectedUser.role) payload.role = editForm.role as any;
                    // Handle email verification toggle via backend-supported boolean
                    const wasVerified = !!selectedUser.email_verified_at;
                    if (editForm.emailVerified !== wasVerified) {
                      payload.emailVerified = editForm.emailVerified;
                    }

                    const selectedPhone = phoneNumbers.find((num) => num.id === editForm.phoneNumberId);
                    const phoneChanged = editForm.phoneNumberId !== (selectedUser.phone_number_id || '');

                    if (editForm.role === 'agent') {
                      if (phoneChanged) {
                        (payload as any).phoneNumberId = selectedPhone?.id;
                        (payload as any).phoneNumber = selectedPhone?.number;
                        (payload as any).phoneNumberProvider = selectedPhone?.provider;
                      }
                    } else if (selectedUser.phone_number_id) {
                      (payload as any).phoneNumberId = null;
                      (payload as any).phoneNumber = null;
                      (payload as any).phoneNumberProvider = null;
                    }

                    if (Object.keys(payload).length === 0) {
                      setShowEditUserModal(false);
                      return;
                    }

                    await adminService.updateUser(selectedUser.id, payload);
                    setShowEditUserModal(false);
                    setSelectedUser(null);
                    fetchUsers();
                  } catch (error) {
                    console.error('Failed to update user:', error);
                  } finally {
                    setUpdatingUser(false);
                  }
                }}
                disabled={
                  updatingUser ||
                  !editForm.name ||
                  !editForm.email
                }
              >
                {updatingUser ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete User Modal (Admin-only) */}
      <Modal
        isOpen={showDeleteUserModal && !!selectedUser}
        onClose={() => setShowDeleteUserModal(false)}
        title="Delete user"
        size="md"
      >
        {selectedUser && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">
              Are you sure you want to delete <span className="font-medium">{selectedUser.name}</span> ({selectedUser.email})?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <Button variant="outlined" onClick={() => setShowDeleteUserModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (deletingUser) return;
                  setDeletingUser(true);
                  try {
                    // Prevent deleting yourself in UI (backend also enforces)
                    if (selectedUser.id === currentUser?.id) {
                      console.error('You cannot delete your own account');
                      setDeletingUser(false);
                      return;
                    }
                    await adminService.deleteUser(selectedUser.id);
                    setShowDeleteUserModal(false);
                    setSelectedUser(null);
                    fetchUsers();
                  } catch (error) {
                    console.error('Failed to delete user:', error);
                  } finally {
                    setDeletingUser(false);
                  }
                }}
              >
                {deletingUser ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Audit Log Details Modal */}
      <Modal
        isOpen={showAuditModal && !!selectedAuditLog}
        onClose={() => setShowAuditModal(false)}
        title="Audit Log Details"
        size="lg"
      >
        {selectedAuditLog && (
          <div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Actor</label>
                    <p className="mt-1 text-sm text-foreground">
                      {selectedAuditLog.actor?.name} ({selectedAuditLog.actor?.email})
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Action</label>
                    <div className="mt-1">{getActionBadge(selectedAuditLog.action)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Entity</label>
                    <p className="mt-1 text-sm text-foreground capitalize">
                      {selectedAuditLog.entity} ({selectedAuditLog.entity_id})
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground">Timestamp</label>
                    <p className="mt-1 text-sm text-foreground">
                      {new Date(selectedAuditLog.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                {selectedAuditLog.metadata && Object.keys(selectedAuditLog.metadata).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">Metadata</label>
                    <div className="bg-muted rounded-lg p-3">
                      <pre className="text-xs text-foreground whitespace-pre-wrap">
                        {JSON.stringify(selectedAuditLog.metadata, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
              
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setShowAuditModal(false)}
                variant="outlined"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminPage;
